/* Assigning a booked service to a technician's next turn. Rendered only while a
 * scheduling plugin is on, and the booking it works from is already normalised —
 * nothing here knows which system it came out of. */
import * as Vue from 'vue';
import { store } from '../store.js';

export const AssignTurn = {
  name: 'AssignTurn',
  setup: function () {
    var a = Vue.computed(function () { return store.ui.assign; });

    var service = Vue.computed(function () {
      return a.value && a.value.serviceId
        ? store.serviceById.value[a.value.serviceId] || null : null;
    });

    // Anyone in today who is free. The booked tech may not be free, so the
    // list stays open rather than forcing whoever the book has on it — but an
    // option that limits the work to certain technicians does take people off
    // it, because that is a rule about the service, not a preference.
    var candidates = Vue.computed(function () {
      if (!a.value) return [];
      var only = a.value.staffIds;
      var queue = store.availableQueue.value.filter(function (s) {
        return !only || !only.length || only.indexOf(s.id) !== -1;
      });
      return queue.map(function (s, i) {
        return {
          staff: s,
          pos: i + 1,
          credit: store.turnCredit(s.id),
          openHalf: store.hasOpenHalf(s.id),
          doesService: !service.value || s.serviceIds.indexOf(service.value.id) !== -1
        };
      });
    });

    var chosen = Vue.computed(function () {
      if (!a.value || !a.value.staffId) return null;
      return store.staffOf(a.value.staffId);
    });

    // Same rule as the service bar: a type nobody free performs cannot be booked.
    var availability = Vue.computed(function () {
      if (!a.value || !a.value.serviceId) return { ok: true };
      return store.serviceAvailability(a.value.serviceId);
    });

    var canAssign = Vue.computed(function () {
      return !!(a.value && a.value.serviceId && a.value.staffId && availability.value.ok);
    });

    // Turns of this type already running, which this booking can be attached
    // to instead of starting a new one.
    var attachable = Vue.computed(function () {
      if (!a.value || !a.value.serviceId) return [];
      return store.attachableTurns(a.value.serviceId).map(function (t) {
        return {
          turn: t,
          tech: t.staffName || (store.staffOf(t.staffId) || {}).name || '',
          elapsed: store.formatDuration(store.now.value - t.startedAt)
        };
      });
    });

    // The booking's own ids go on the link, not just its names. Editing an
    // appointment hands its services brand new line ids, and reconcileTurns
    // re-finds the square by appointment + sourceServiceId; without it the
    // match falls back to comparing service names, and clientId is never
    // recorded at all. The drag paths always passed these — the two modal
    // paths did not, so a turn attached from here was the one that came
    // adrift when the booking was edited.
    function attach(turnId) {
      store.attachToTurn(turnId, {
        appointmentId: a.value.appointmentId,
        appointmentServiceId: a.value.appointmentServiceId,
        sourceServiceId: a.value.sourceServiceId,
        clientId: a.value.clientId,
        clientName: a.value.clientName,
        sourceServiceName: a.value.sourceServiceName
      });
    }

    /* When the timer starts, asked only when the two answers differ.
     *
     * A booking normally keeps its own start — a client booked at ten and
     * seated at ten past is ten minutes in, not starting. `store.assignLateStart`
     * is the case where that is provably wrong: the technician the sheet is on
     * was still with the previous client at ten, so ten is a time they were
     * demonstrably working on somebody else. See js/rules/overrun.js.
     *
     * Null the rest of the time, and the section is not drawn — which is most
     * of the time. This is a question about an exception and a sheet that asks
     * it on every seating is a sheet nobody reads.
     *
     * The words are built here rather than in the store because they are only
     * words. What is being decided is two timestamps, and both of them are on
     * the object already.
     */
    /* Minutes in words, not the board's `0:20`. Everywhere else a duration sits
     * on a square where a clock face is what is wanted; here it sits inside a
     * sentence, and "ran 20:00 over" reads as a time of day for as long as it
     * takes to reparse it. Never below a minute — the question is not asked
     * inside the grace; see OVERRUN_GRACE_MS. */
    function mins(ms) {
      var n = Math.max(1, Math.round(ms / 60000));
      return n + (n === 1 ? ' minute' : ' minutes');
    }

    var lateStart = Vue.computed(function () {
      var l = store.assignLateStart.value;
      if (!l) return null;
      var prev = l.prevClient
        ? l.prevClient + '’s ' + (l.prevService || 'service')
        : (l.prevService || 'The previous service');
      return {
        prev: prev,
        over: mins(l.over),
        freed: store.formatClock(l.freedAt),
        booked: store.formatClock(l.bookedAt),
        // What the booked answer would put on the clock the moment the square
        // lands. Measured from now, not from when the technician came free:
        // it is the number the desk would see on the square.
        already: mins(Math.max(0, store.now.value - l.bookedAt))
      };
    });

    function setStartFrom(which) { if (a.value) a.value.startFrom = which; }

    return {
      a: a,
      schedulingName: store.schedulingName,
      lateStart: lateStart,
      setStartFrom: setStartFrom,
      clockNow: Vue.computed(function () { return store.formatClock(store.now.value); }),
      weightBadge: store.weightBadge,
      weightLabel: store.weightLabel,
      service: service,
      services: store.activeServices,
      candidates: candidates,
      chosen: chosen,
      canAssign: canAssign,
      ui: store.ui,
      availability: availability,
      attachable: attachable,
      attach: attach,
      pickStaff: store.setAssignStaff,
      noColor: store.defaultServiceColor,
      formatCredit: store.formatCredit,
      close: store.closeAssign,
      confirm: store.confirmAssign,
      noteWarning: store.assignNoteWarning
    };
  },
  template: [
    '<div v-if="a" class="modal-scrim" @click.self="close">',
    '  <div class="sheet sheet--wide" role="dialog" aria-modal="true">',
    '    <div class="sheet__head" :style="{ \'--svc\': service ? service.color : noColor }">',
    '      <span class="sheet__letter">{{ service ? service.abbrev : "?" }}</span>',
    '      <div>',
    '        <h3>Assign turn for {{ a.clientName }}&rsquo;s {{ a.sourceServiceName }}?<client-note :client-id="a.clientId" /></h3>',
    '        <p v-if="a.bookedTechName">Booked with {{ a.bookedTechName }}</p>',
    '        <p v-else>No technician on the booking</p>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <h4 class="assign__label">Counts as</h4>',
    '      <div class="svc-toggles">',
    '        <button v-for="svc in services" :key="svc.id" type="button" class="chip chip--sm"',
    '          :class="{ \'chip--on\': a.serviceId === svc.id }" :style="{ \'--chip\': svc.color }"',
    '          @click="a.serviceId = svc.id">',
    '          <span class="chip__letter">{{ svc.abbrev }}</span>',
    '          <span class="chip__name">{{ svc.name }}</span>',
    '          <span v-if="svc.turnWeight !== 1" class="chip__half"',
    '            :class="{ \'is-zero\': svc.turnWeight === 0 }"',
    '            :title="weightLabel(svc.turnWeight)">{{ weightBadge(svc.turnWeight) }}</span>',
    '        </button>',
    '      </div>',
    '      <p v-if="!a.serviceId" class="warn-line">',
    '        This {{ schedulingName }} service is not mapped to a turn type &mdash; pick one.</p>',
    '      <p v-else-if="!availability.ok" class="warn-line">',
    '        {{ availability.reason === "busy"',
    '           ? "Everyone who does " + availability.serviceName + " is mid-service."',
    '           : "Nobody in today is set up for " + availability.serviceName + "." }}',
    '        This turn cannot be assigned.</p>',
    '    </div>',
    '',
    '    <div v-if="attachable.length" class="assign__section">',
    '      <h4 class="assign__label">Already running &mdash; attach instead</h4>',
    '      <div class="assign__techs">',
    '        <button v-for="c in attachable" :key="c.turn.id" type="button" class="tech-pick tech-pick--attach"',
    '          @click="attach(c.turn.id)">',
    '          <span class="tech-pick__live"></span>',
    '          <span class="tech-pick__name">{{ c.tech }}</span>',
    '          <span class="tech-pick__meta">{{ c.elapsed }}</span>',
    '        </button>',
    '      </div>',
    '      <p class="assign__note">Links this booking to a turn that is already under way, rather than starting a new one.</p>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <h4 class="assign__label">{{ attachable.length ? "Or start a new turn" : "Technician" }}</h4>',
    '      <p v-if="a.staffIds && a.staffIds.length" class="assign__note">',
    '        {{ a.options.join(", ") || "An option on this booking" }} &mdash; only the technicians set up for it are listed.</p>',
    '      <p v-if="!candidates.length" class="warn-line">Everyone in today is on a service.</p>',
    '      <div v-else class="assign__techs">',
    '        <button v-for="c in candidates" :key="c.staff.id" type="button" class="tech-pick"',
    '          :class="{ \'is-on\': a.staffId === c.staff.id, \'is-off-service\': !c.doesService }"',
    '          @click="pickStaff(c.staff.id)">',
    '          <span class="tech-pick__pos">{{ c.pos }}</span>',
    '          <span class="tech-pick__name">{{ c.staff.name }}</span>',
    '          <span class="tech-pick__meta">',
    '            {{ formatCredit(c.credit) }}',
    '            <span v-if="c.openHalf" class="tech-pick__half">&frac12;</span>',
    '          </span>',
    '        </button>',
    '      </div>',
    '      <p v-if="chosen && service && chosen.serviceIds.indexOf(service.id) === -1"',
    '        class="warn-line">{{ chosen.name }} does not list {{ service.name }}.</p>',
    '    </div>',
    '',
    /* Where the clock starts. Only drawn when the technician on the sheet ran
       past their last client's time and into this booking's — see `lateStart`
       above. Below the technician list, because it is a question about whoever
       is picked there and moving the pick re-asks it. */
    '    <div v-if="lateStart" class="assign__section assign__section--late">',
    '      <h4 class="assign__label">Start the timer</h4>',
    /* The reason first. "Start now or at the booked time" is not answerable
       without it, and the desk can check this sentence against their own
       memory of the last hour. */
    '      <p class="assign__why">{{ lateStart.prev }} ran {{ lateStart.over }} over',
    '        and finished at {{ lateStart.freed }} &mdash; after this one was due to start.</p>',
    '      <div class="assign__start" role="radiogroup" aria-label="When this service started">',
    '        <button type="button" class="assign__start-opt"',
    '          :class="{ \'is-on\': a.startFrom === \'now\' }"',
    '          role="radio" :aria-checked="a.startFrom === \'now\'"',
    '          @click="setStartFrom(\'now\')">',
    '          <span class="assign__start-when">Now</span>',
    '          <span class="assign__start-at">{{ clockNow }} · a fresh clock</span>',
    '        </button>',
    '        <button type="button" class="assign__start-opt"',
    '          :class="{ \'is-on\': a.startFrom === \'booked\' }"',
    '          role="radio" :aria-checked="a.startFrom === \'booked\'"',
    '          @click="setStartFrom(\'booked\')">',
    '          <span class="assign__start-when">Booked time</span>',
    '          <span class="assign__start-at">{{ lateStart.booked }} ·',
    '            {{ lateStart.already }} already gone</span>',
    '        </button>',
    '      </div>',
    /* Said before the choice is made rather than discovered afterwards: the
       book is the last word on this square's time, and an answer given here is
       an answer until the appointment itself moves. */
    '      <p class="assign__note">Re-time this appointment in {{ schedulingName }}',
    '        and that start replaces whichever is picked here.</p>',
    '    </div>',
    '',
    '    <div v-if="ui.requestsOn" class="assign__section">',
    '      <label class="switch">',
    '        <input type="checkbox" v-model="a.requested" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">&#9733; Client request</span>',
    '      </label>',
    '    </div>',
    '',
    /* Under turn order this tap changes the book, so a note naming anybody is said
       here, before it does. See `assignNoteWarning`. */
    '    <p v-if="noteWarning" class="warn-line">',
    '      {{ a.clientName }}&rsquo;s note names {{ noteWarning.named.join(", ") }}: {{ noteWarning.notes }}</p>',
    '',
    '    <div class="sheet__actions">',
    '      <button type="button" class="btn btn--primary btn--lg" :disabled="!canAssign"',
    '        @click="confirm">',
    '        Assign to {{ chosen ? chosen.name : "…" }}</button>',
    '      <button type="button" class="btn btn--quiet" @click="close">Cancel</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
